import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useUpiTransactions } from "../transactions.hook";
import { upiTransactionsColumns } from "./UpiTransactions.constants";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.info.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.info.lighter}08 0%, ${theme.palette.info.light}05 100%)`,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 25px ${theme.palette.info.light}20`,
    border: `1px solid ${theme.palette.info.light}60`,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const MetricCard = styled(Box)(({ theme, color }) => {
  const getCardColors = () => {
    switch (color) {
      case "success":
        return {
          main: theme.palette.success.main,
          light: theme.palette.success.light,
          lighter: theme.palette.success.lighter,
        };
      case "warning":
        return {
          main: theme.palette.warning.main,
          light: theme.palette.warning.light,
          lighter: theme.palette.warning.lighter,
        };
      case "error":
        return {
          main: theme.palette.error.main,
          light: theme.palette.error.light,
          lighter: theme.palette.error.lighter,
        };
      case "info":
        return {
          main: theme.palette.info.main,
          light: theme.palette.info.light,
          lighter: theme.palette.info.lighter,
        };
      default:
        return {
          main: theme.palette.primary.main,
          light: theme.palette.primary.light,
          lighter: theme.palette.primary.lighter,
        };
    }
  };

  const colors = getCardColors();

  return {
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${colors.lighter}15 50%, ${colors.light}08 100%)`,
    border: `1px solid ${colors.light}30`,
    textAlign: "center",
    height: "100%",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 8px 25px ${colors.light}20`,
      border: `1px solid ${colors.light}50`,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background: `linear-gradient(90deg, ${colors.main} 0%, ${colors.light} 100%)`,
      borderRadius: `${theme.shape.borderRadius * 1.5}px ${
        theme.shape.borderRadius * 1.5
      }px 0 0`,
    },
  };
});

export const UpiTransactionsTab = () => {
  const team = useSelectedTeam();
  const teamId = team?.teamId;
  const [dateRange, setDateRange] = useState("today");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Memoize the teamId to prevent unnecessary re-renders
  const currentTeamId = useMemo(() => {
    return teamId;
  }, [teamId]);

  const {
    data: responseData = {},
    isFetching,
    error,
    refetch,
  } = useUpiTransactions({
    teamId: currentTeamId,
    dateRange: dateRange,
    page: page + 1, // API expects 1-based index
    limit: pageSize,
    enabled: !!currentTeamId,
  });

  // Extract transactions and total count
  const { tableData, totalCount } = useMemo(() => {
    // Handle paginated response structure from hook (which matches controller return)
    const transactions = Array.isArray(responseData.data)
      ? responseData.data
      : [];
    const total = responseData.total || transactions.length;

    // Apply client-side search if needed (only on current page data as server doesn't support search yet)
    // This matches TransactionsList behavior where search filters the current data
    if (!searchText.trim()) {
      return { tableData: transactions, totalCount: total };
    }

    const search = searchText.trim().toLowerCase();
    const filtered = transactions.filter((item) => {
      const fields = [
        "id",
        "vpa",
        "description",
        "status",
        "method",
        "currency",
        "email",
        "contact",
      ];
      return (
        fields.some((key) => {
          const val = item[key];
          const content = val?.toString().toLowerCase();
          return content?.includes(search);
        }) ||
        (item.notes?.chargerId &&
          item.notes.chargerId.toLowerCase().includes(search)) ||
        (item.notes?.connectorId &&
          item.notes.connectorId.toString().includes(search))
      );
    });

    return { tableData: filtered, totalCount: total };
  }, [responseData, searchText]);

  const dateRangeOptions = [
    { value: "today", label: "Today" },
    { value: "last2days", label: "Last 2 Days" },
    { value: "last7days", label: "Last 7 Days" }, // Added for consistency with backend
  ];

  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
    setPage(0); // Reset to first page on filter change
  };

  const handleSearching = (event) => {
    setSearchText(event.target.value);
  };

  // Calculate stats from the current page (or we could fetch separate stats endpoint, but for now match existing pattern)
  // Note: These stats will only reflect the current page of data in server-side pagination mode
  const statistics = useMemo(() => {
    const dataToUse = tableData; // Use the data visible in table

    const totalTransactions = dataToUse.length;
    const refundedTransactions = dataToUse.filter(
      (txn) => txn.status === "refunded"
    ).length;
    const failedTransactions = dataToUse.filter(
      (txn) => txn.status === "failed"
    ).length;
    const totalCapturedAmount = dataToUse
      .filter((txn) => txn.status === "captured")
      .reduce((sum, txn) => sum + (txn.amount || 0), 0);
    const totalRefundedAmount = dataToUse.reduce((sum, txn) => {
      if (
        txn.amount_refunded &&
        txn.amount_refunded > 0 &&
        txn.refund_status !== "full"
      ) {
        return sum + txn.amount_refunded;
      } else if (
        txn.status === "refunded" &&
        txn.amount &&
        txn.refund_status !== "full"
      ) {
        return sum + txn.amount;
      }
      return sum;
    }, 0);

    return {
      totalTransactions,
      refundedTransactions,
      failedTransactions,
      totalCapturedAmount,
      totalRefundedAmount,
    };
  }, [tableData]);

  if (!currentTeamId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="warning.main">
          Please select a team to view UPI transactions
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Card sx={{ p: 2, mb: 2, bgcolor: "error.lighter" }}>
          <Typography variant="body2" color="error.main">
            Unable to load UPI transactions:{" "}
            {error.message || "Something went wrong. Please try again."}
          </Typography>
        </Card>
      )}

      {/* UPI Transactions Summary - derived from current page view */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="success">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "success.dark", mb: 1 }}
            >
              Transactions (Page)
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "success.dark" }}
            >
              {statistics.totalTransactions}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="warning">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "warning.dark", mb: 1 }}
            >
              Refunded (Page)
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "warning.dark" }}
            >
              {statistics.refundedTransactions}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="error">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "error.dark", mb: 1 }}
            >
              Failed (Page)
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "error.dark" }}
            >
              {statistics.failedTransactions}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="success">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "success.dark", mb: 1 }}
            >
              Captured Amount
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "success.dark" }}
            >
              ₹{statistics.totalCapturedAmount / 100}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="error">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "error.dark", mb: 1 }}
            >
              Refunded Amount
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "error.dark" }}
            >
              ₹{statistics.totalRefundedAmount / 100}
            </Typography>
          </MetricCard>
        </Grid>
      </Grid>

      <StyledListCard sx={{ width: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
          spacing={2}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}>
            <CommonSearchbar
              placeholder="Search in current page..."
              searchFunc={handleSearching}
              sx={{ maxWidth: 300 }}
            />

            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel id="date-range-select-label">Date Range</InputLabel>
              <Select
                labelId="date-range-select-label"
                value={dateRange}
                label="Date Range"
                onChange={handleDateRangeChange}
              >
                {dateRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Refresh data">
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={isFetching}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                    "&:disabled": {
                      bgcolor: "grey.300",
                      color: "grey.500",
                    },
                  }}
                >
                  <Icon icon={refreshFill} width={20} height={20} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>

        <BaseTable
          rows={tableData}
          columns={upiTransactionsColumns}
          loading={isFetching}
          getRowId={(row) => row.id}
          paginationMode="server"
          rowCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          showExportCsvBtn={true}
          csvMorphemes="upi-transactions"
          csvColumns={upiTransactionsColumns}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              overflow: "hidden !important",
              lineHeight: "20px !important",
              whiteSpace: "normal !important",
            },
          }}
        />
      </StyledListCard>
    </Box>
  );
};
